import type { AnnouncementScope } from '../api.js';
import type { AdminClientContext, AdminHttpClient } from './client.js';

type MethodKeys =
  | 'listAnnouncements'
  | 'createAnnouncement'
  | 'patchAnnouncement'
  | 'publishAnnouncement'
  | 'archiveAnnouncement';

/** The transport method triple for one authoring surface. The scope→methods map
 *  is declared once here so the screens never spell a route name. */
const TRANSPORT = {
  tenant: {
    list: 'listAnnouncements',
    create: 'createAnnouncement',
    patch: 'patchAnnouncement',
    publish: 'publishAnnouncement',
    archive: 'archiveAnnouncement',
  },
  partner: {
    list: 'listPartnerAnnouncements',
    create: 'createPartnerAnnouncement',
    patch: 'patchPartnerAnnouncement',
    publish: 'publishPartnerAnnouncement',
    archive: 'archivePartnerAnnouncement',
  },
  platform: {
    list: 'listPlatformAnnouncements',
    create: 'createPlatformAnnouncement',
    patch: 'patchPlatformAnnouncement',
    publish: 'publishPlatformAnnouncement',
    archive: 'archivePlatformAnnouncement',
  },
} as const satisfies Record<
  AnnouncementScope,
  Record<'list' | 'create' | 'patch' | 'publish' | 'archive', string>
>;

export function buildAnnouncementsMethods(ctx: AdminClientContext) {
  const { transport, token, idemKey } = ctx;

  return {
    listAnnouncements: (scope, params) =>
      transport[TRANSPORT[scope].list](token(), params),
    createAnnouncement: (scope, input, idempotencyKey) =>
      transport[TRANSPORT[scope].create](token(), {
        ...input,
        idempotencyKey: idempotencyKey ?? idemKey(),
      }),
    patchAnnouncement: (scope, input, idempotencyKey) =>
      transport[TRANSPORT[scope].patch](token(), {
        ...input,
        idempotencyKey: idempotencyKey ?? idemKey(),
      }),
    publishAnnouncement: (scope, id, idempotencyKey) =>
      transport[TRANSPORT[scope].publish](token(), {
        id,
        idempotencyKey: idempotencyKey ?? idemKey(),
      }),
    archiveAnnouncement: (scope, id, idempotencyKey) =>
      transport[TRANSPORT[scope].archive](token(), {
        id,
        idempotencyKey: idempotencyKey ?? idemKey(),
      }),
  } satisfies Pick<AdminHttpClient, MethodKeys>;
}
